
import {
  IClientDetail,
  IClientDetailData,
  IClientExtradata,
  ILimitData,
  IThirdcode,
  IUpdateClient,
} from "@/entities";
import { IGenericResponse } from "@/services";
import { ValidationObserver } from "vee-validate";
import Multiselect from "vue-multiselect";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action } from "vuex-class";
import ExtradataTable from "@/components/client/ExtradataTable.vue";
import AddEditExtradataModal from "@/components/client/AddEditExtradataModal.vue";
import ThirdcodeTable from "@/components/thirdcode/ThirdcodeTable.vue";
import LimitsTable from "@/components/limits/LimitsTable.vue";

@Component({
  name: "client-editable-detail",
  components: {
    AddEditExtradataModal,
    ExtradataTable,
    ValidationObserver,
    Multiselect,
    ThirdcodeTable,
    LimitsTable,
  },
  props: ["detail"],
})
export default class ClientEditableDetail extends Vue {
  @Prop() public detail!: IClientDetail;

  @Action("saveUpdateClient") saveUpdateClient!: (
    updateClient: IUpdateClient
  ) => Promise<IGenericResponse<IClientDetailData> | undefined>;
  @Action("updateStatusClient") updateStatusClient!: (id: number) => Promise<void>;

  public oidSolInput?: string | null = null;
  public codeSolInput?: string | null = null;
  public idSmartcashInput?: string | null = null;
  public uniqueCodeInput?: string | null = null;
  public descriptionInput?: string | null = null;
  public extradata?: IClientExtradata | null = null;
  public limits?: ILimitData | null = null;
  public thirdCodes?: IThirdcode[] = [];

  constructor() {
    super();
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  async save(): Promise<void> {
    const updateClient: IUpdateClient = {
      id: this.detail.id!,
      oidSol: this.oidSolInput!,
      codeSol: this.codeSolInput!,
      uniqueCode: this.uniqueCodeInput!,
      description: this.descriptionInput!,
      idSmartcash: this.idSmartcashInput!,
      active: this.detail.active!,
      country: this.detail.country!.id,
      extradata: this.detail.extradata,
      thirdcodes: this.detail.thirdcodes,
      limits: this.detail.limits,
    };

    const res = await this.saveUpdateClient(updateClient);

    if (res) {
      this.makeToast("success", this.$t(res.message) as string);
    }
  }

  mounted(): void {
    this.intiializeFormData();
  }

  public intiializeFormData(): void {
    this.oidSolInput = this.detail?.oidSol;
    this.codeSolInput = this.detail?.codeSol;
    this.idSmartcashInput = this.detail?.idSmartcash;
    this.uniqueCodeInput = this.detail?.uniqueCode;
    this.descriptionInput = this.detail?.description;
    this.thirdCodes = this.detail?.thirdcodes;
  }

  async synchronize(): Promise<void> {
    const res = await this.$services.client.syncClient(this.detail.id!);

    if (!res) {
      this.makeToastError();
      return;
    }

    if (!res?.success) {
      this.makeToastErrorWithDescription(res?.message || "unexpectedError.description");
      if (this.detail.active) {
        await this.showModalErrorOnSync();
      }
      return;
    }
    const clientData = res.data!;
    this.descriptionInput = clientData.description;
    this.oidSolInput = clientData.oidSol;
    this.codeSolInput = clientData.codeSol;
    this.thirdCodes = clientData.thirdcodes || [];

    this.idSmartcashInput = clientData.idSmartcash;
    this.uniqueCodeInput = clientData.uniqueCode;

    this.$emit("fetch-client-required");

    this.makeToast("success", this.$t("validation.messages.client.updated").toString());
  }

  async showModalErrorOnSync(): Promise<void> {
    const title = this.$t("validation.messages.cannot.synchronize.client.disclaimer.title");
    const message = this.$t("validation.messages.cannot.synchronize.client.disclaimer");
    const question = this.$t("validation.messages.cannot.synchronize.client.disclaimer.question");
    const titleWithQuestion = `${title}<br>${question}`;

    const swal = await this.$swal({
      title: titleWithQuestion,
      text: message,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes"),
      cancelButtonText: this.$t("label.no"),
    });
    if (swal.isConfirmed) {
      await this.updateStatusClient(this.detail.id!);
    }
    this.$emit("fetch-client-required");
  }

  makeToastErrorWithDescription(messageError: string): void {
    this.makeToast("danger", this.$t(messageError));
  }

  makeToastError(): void {
    this.makeToast("danger", this.$t("unexpectedError.description"));
  }
}
